import React, { memo, useCallback } from 'react';
import { MAX_FADE_DURATION_SECONDS } from '../../constants';
import { AudioItem } from '../../items/audio/audio-item-type';
import { Slider, ValueInputField } from '@crabs-ai/ds-react';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useFps, useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { InspectorSubLabel } from '../components/inspector-label';
import { useSelectionValues } from '../utils/use-selection-values';
import { useTranslations } from 'next-intl';

const AudioFadeControlsUnmemoized: React.FC<{
  fadeInDuration: number;
  fadeOutDuration: number;
  itemId: string;
  durationInFrames: number;
}> = ({ fadeInDuration, fadeOutDuration, itemId, durationInFrames }) => {
  const t = useTranslations();

  const { fps } = useFps();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isFadeInMixed } = useSelectionValues<AudioItem, number>(
    itemId,
    (item) => item.audioFadeInDurationInSeconds,
  );
  const { isMixed: isFadeOutMixed } = useSelectionValues<AudioItem, number>(
    itemId,
    (item) => item.audioFadeOutDurationInSeconds,
  );

  const setFadeInDuration = useCallback(
    (newFadeInDuration: number, commitToUndoStack: boolean) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as AudioItem;
            if (prev.audioFadeInDurationInSeconds === newFadeInDuration) {
              return prev;
            }
            return {
              ...prev,
              audioFadeWindow:undefined,
              audioFadeInDurationInSeconds: newFadeInDuration,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const setFadeOutDuration = useCallback(
    (newFadeOutDuration: number, commitToUndoStack: boolean) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as AudioItem;
            if (prev.audioFadeOutDurationInSeconds === newFadeOutDuration) {
              return prev;
            }
            return {
              ...prev,
              audioFadeWindow:undefined,
              audioFadeOutDurationInSeconds: newFadeOutDuration,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const handleFadeInChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      setFadeInDuration(value, commitToUndoStack);
    },
    [setFadeInDuration],
  );

  const handleFadeOutChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      setFadeOutDuration(value, commitToUndoStack);
    },
    [setFadeOutDuration],
  );

  // item duration과 fade 제약을 기반으로 최대 fade duration 계산
  const clipDurationInSeconds = durationInFrames / fps;

  // 각 fade는 다른 fade와 겹칠 수 없으며 함께 clip duration을 초과할 수 없음
  // 시각적 중복을 방지하기 위해 최소 1 frame의 간격을 유지
  const minGap = 1 / fps;
  const maxFadeInDuration = Math.max(
    0,
    Math.min(
      MAX_FADE_DURATION_SECONDS,
      clipDurationInSeconds - fadeOutDuration - minGap,
    ),
  );

  const maxFadeOutDuration = Math.max(
    0,
    Math.min(
      MAX_FADE_DURATION_SECONDS,
      clipDurationInSeconds - fadeInDuration - minGap,
    ),
  );

  return (
    <div className="flex flex-col gap-4xs">
      <div>
        <InspectorSubLabel>
          {t('Editor.inspector.fade-in-effect')}
        </InspectorSubLabel>
        <div className="flex w-full items-center gap-3">
          <Slider
            value={[fadeInDuration]}
            onValueChange={(values) =>
              handleFadeInChange(values[0] ?? 0, false)
            }
            onValueCommit={(values) => handleFadeInChange(values[0] ?? 0, true)}
            min={0}
            max={maxFadeInDuration}
            step={0.1}
            className="flex-1"
            isShowTooltip={false}
            aria-label={`Fade In: ${fadeInDuration.toFixed(1)}s`}
          />
          <ValueInputField
            value={Number(fadeInDuration.toFixed(1))}
            setValue={(update) =>
              handleFadeInChange(update.num, update.commitToUndoStack)
            }
            min={0}
            max={maxFadeInDuration}
            step={0.1}
            isUnit="s"
            hasMixedValue={isFadeInMixed}
            accessibilityLabel="Fade In Duration"
            className="w-[96px]"
          />
        </div>
      </div>
      <div>
        <InspectorSubLabel>
          {t('Editor.inspector.fade-out-effect')}
        </InspectorSubLabel>
        <div className="flex w-full items-center gap-3">
          <Slider
            value={[fadeOutDuration]}
            onValueChange={(values) =>
              handleFadeOutChange(values[0] ?? 0, false)
            }
            onValueCommit={(values) =>
              handleFadeOutChange(values[0] ?? 0, true)
            }
            min={0}
            max={maxFadeOutDuration}
            step={0.1}
            className="flex-1"
            isShowTooltip={false}
            aria-label={`Fade Out: ${fadeOutDuration.toFixed(1)}s`}
          />
          <ValueInputField
            value={Number(fadeOutDuration.toFixed(1))}
            setValue={(update) =>
              handleFadeOutChange(update.num, update.commitToUndoStack)
            }
            min={0}
            max={maxFadeOutDuration}
            step={0.1}
            isUnit="s"
            hasMixedValue={isFadeOutMixed}
            accessibilityLabel="Fade Out Duration"
            className="w-[96px]"
          />
        </div>
      </div>
    </div>
  );
};

export const AudioFadeControls = memo(AudioFadeControlsUnmemoized);
